import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../../api';

export const useAcademicData = (tenantId, token, includeCompleted = false, onContextChange) => {
  const authHeaders = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );
  
  const [activeTab, setActiveTab] = useState('active');
  const [academicYears, setAcademicYears] = useState([]);
  const [completedAcademicYears, setCompletedAcademicYears] = useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [isSemesterBased, setIsSemesterBased] = useState(false);
  const [semesters, setSemesters] = useState([]);
  const [completedSemesters, setCompletedSemesters] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState('');
  const [terms, setTerms] = useState([]);
  const [completedTerms, setCompletedTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState('');
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [loading, setLoading] = useState(false);
  const [programTypes, setProgramTypes] = useState([]);
  const [selectedProgramTypeId, setSelectedProgramTypeId] = useState('');
  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState('');
  const [programMapping, setProgramMapping] = useState(null);

  const fetchProgramTypes = useCallback(async () => {
    if (!tenantId) return;
    try {
      const res = await api.get('/api/program-types', {
        params: { tenantId, status: 'active' },
        headers: authHeaders
      });
      const list = res?.data?.programTypes || [];
      setProgramTypes(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('[AcademicData] Error fetching program types:', err);
      setProgramTypes([]);
    }
  }, [tenantId, authHeaders]);

  const fetchPrograms = useCallback(async () => {
    if (!tenantId || !selectedProgramTypeId) return;
    try {
      const res = await api.get('/api/programs', {
        params: { tenantId, programTypeId: selectedProgramTypeId, status: 'active' },
        headers: authHeaders
      });
      const list = res?.data?.programs || [];
      setPrograms(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('[AcademicData] Error fetching programs:', err);
      setPrograms([]);
    }
  }, [tenantId, authHeaders, selectedProgramTypeId]);

  const fetchActiveAcademicYears = useCallback(async () => {
    if (!tenantId) return;
    try {
      setLoading(true);
      const res = await api.get(
        `/api/academic-year/all-active?tenantId=${tenantId}`,
        { headers: authHeaders }
      );
      let years = res?.data?.academicYears || res?.data?.activeAcademicYears || [];
      if (!Array.isArray(years) && years) years = [years];
      setAcademicYears(years);
    } catch (err) {
      console.error('[AcademicData] Error fetching active academic years:', err);
      setAcademicYears([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, authHeaders]);

  const fetchCompletedAcademicYears = useCallback(async () => {
    if (!tenantId || !includeCompleted) return;
    try {
      setLoading(true);
      const res = await api.get(
        `/api/academic-year/all-completed?tenantId=${tenantId}`,
        { headers: authHeaders }
      );
      let years = res?.data?.academicYears || [];
      if (!Array.isArray(years) && years) years = [years];
      setCompletedAcademicYears(years);
    } catch (err) {
      console.error('[AcademicData] Error fetching completed academic years:', err);
      setCompletedAcademicYears([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, authHeaders, includeCompleted]);

  const fetchSemesters = useCallback(async () => {
    if (!selectedAcademicYear) return;
    try {
      setLoading(true);
      const res = await api.get(
        `/api/classManager/semesters/${selectedAcademicYear}`,
        { headers: authHeaders }
      );
      const sems = res.data.semesters || [];
      if (activeTab === 'completed') {
        setCompletedSemesters(sems);
      } else {
        setSemesters(sems);
      }
      setIsSemesterBased(true);
    } catch (err) {
      console.warn('[AcademicData] Error fetching semesters or not semester-based:', err);
      if (activeTab === 'completed') {
        setCompletedSemesters([]);
      } else {
        setSemesters([]);
      }
      setIsSemesterBased(false);
    } finally {
      setLoading(false);
    }
  }, [selectedAcademicYear, authHeaders, activeTab]);

  const fetchTerms = useCallback(async () => {
    if (!selectedAcademicYear) return;
    try {
      setLoading(true);
      const res = await api.get(
        `/api/academic-year/${selectedAcademicYear}/terms?tenantId=${tenantId}`,
        { headers: authHeaders }
      );
      const termList = res.data.terms || [];
      if (activeTab === 'completed') {
        setCompletedTerms(termList);
      } else {
        setTerms(termList);
      }
    } catch (err) {
      console.warn('[AcademicData] Error fetching terms:', err);
      if (activeTab === 'completed') {
        setCompletedTerms([]);
      } else {
        setTerms([]);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedAcademicYear, tenantId, authHeaders, activeTab]);

  const fetchClasses = useCallback(async () => {
    if (!selectedAcademicYear) {
      setClasses([]);
      setSelectedClassId('');
      return;
    }
    
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedProgramId) params.append('programId', selectedProgramId);
      if (selectedProgramTypeId) params.append('programTypeId', selectedProgramTypeId);
      
      if (isSemesterBased && selectedSemester) {
        params.append('semesterId', selectedSemester);
      } else if (!isSemesterBased && selectedTerm) {
        params.append('termId', selectedTerm);
      }

      const response = await api.get(
        `/api/classManager/classes-for-academic-year/${selectedAcademicYear}?${params.toString()}`,
        { headers: authHeaders }
      );
      
      const fetchedClasses = response.data?.classes || response.data || [];
      setClasses(Array.isArray(fetchedClasses) ? fetchedClasses : []);
    } catch (err) {
      console.error('Error fetching classes:', err);
      setClasses([]);
    } finally {
      setLoading(false);
    }
  }, [
    selectedAcademicYear,
    selectedProgramId,
    selectedProgramTypeId,
    isSemesterBased,
    selectedSemester,
    selectedTerm,
    authHeaders
  ]);

  const fetchProgramMapping = useCallback(async () => {
    if (!tenantId || !selectedProgramId || !selectedProgramTypeId || !selectedAcademicYear) {
      setProgramMapping(null);
      return;
    }

    // For semester-based, need semester; for term-based, need term
    if (isSemesterBased && !selectedSemester) {
      setProgramMapping(null);
      return;
    }
    if (!isSemesterBased && !selectedTerm) {
      setProgramMapping(null);
      return;
    }

    try {
      setLoading(true);
      const params = {
        tenantId,
        programId: selectedProgramId,
        programTypeId: selectedProgramTypeId,
        academicYearId: selectedAcademicYear
      };
      
      if (isSemesterBased && selectedSemester) {
        params.semesterId = selectedSemester;
      } else if (!isSemesterBased && selectedTerm) {
        params.termId = selectedTerm;
      }
      
      const res = await api.get('/api/program-subject-mappings/grouped', {
        params,
        headers: authHeaders
      });
      setProgramMapping(res?.data?.mapping || null);
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('[AcademicData] Error fetching program subject mapping:', err);
      }
      setProgramMapping(null);
    } finally {
      setLoading(false);
    }
  }, [
    tenantId,
    selectedProgramId,
    selectedProgramTypeId,
    selectedAcademicYear,
    selectedSemester,
    selectedTerm,
    isSemesterBased,
    authHeaders
  ]);

  const getSelectedAcademicYearData = useCallback(() => {
    const allYears = activeTab === 'active' ? academicYears : completedAcademicYears;
    return allYears.find(
      (y) => y._id === selectedAcademicYear || y.id === selectedAcademicYear
    ) || {};
  }, [selectedAcademicYear, academicYears, completedAcademicYears, activeTab]);

  const getSelectedSemesterData = useCallback(() => {
    const allSemesters = activeTab === 'active' ? semesters : completedSemesters;
    return allSemesters.find(
      (s) => s._id === selectedSemester || s.id === selectedSemester
    ) || {};
  }, [selectedSemester, semesters, completedSemesters, activeTab]);

  const getSelectedTermData = useCallback(() => {
    const allTerms = activeTab === 'active' ? terms : completedTerms;
    return allTerms.find(
      (t) => t._id === selectedTerm || t.id === selectedTerm
    ) || {};
  }, [selectedTerm, terms, completedTerms, activeTab]);

  const isViewOnly = useCallback(() => {
    if (activeTab === 'completed') return true;
    if (isSemesterBased && selectedSemester) {
      const semesterData = getSelectedSemesterData();
      return semesterData.status === 'completed';
    }
    return false;
  }, [activeTab, isSemesterBased, selectedSemester, getSelectedSemesterData]);

  const switchTab = (tab) => {
    setActiveTab(tab);
    setSelectedAcademicYear('');
    setSelectedSemester('');
    setSelectedTerm('');
    setClasses([]);
    setSelectedClassId('');
  };

  // Effects
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchProgramTypes();
    fetchActiveAcademicYears();
    if (includeCompleted) {
      fetchCompletedAcademicYears();
    }
  }, [fetchProgramTypes, fetchActiveAcademicYears, fetchCompletedAcademicYears, includeCompleted]);

  useEffect(() => {
    if (selectedProgramTypeId) {
      fetchPrograms();
    }
  }, [selectedProgramTypeId, fetchPrograms]);

  useEffect(() => {
    if (!selectedAcademicYear) return;
    const ayData = getSelectedAcademicYearData();
    
    // Always fetch both to determine type
    fetchSemesters();
    fetchTerms();
    
    if (ayData.isSemesterBased) {
      setIsSemesterBased(true);
    } else {
      setIsSemesterBased(false);
    }
  }, [selectedAcademicYear, getSelectedAcademicYearData, fetchSemesters, fetchTerms]);

  // Auto-select first semester/term when available
  useEffect(() => {
    if (isSemesterBased && semesters.length > 0 && !selectedSemester) {
      setSelectedSemester(semesters[0]._id || semesters[0].id);
    }
  }, [isSemesterBased, semesters, selectedSemester]);

  useEffect(() => {
    if (!isSemesterBased && terms.length > 0 && !selectedTerm) {
      setSelectedTerm(terms[0]._id || terms[0].id);
    }
  }, [isSemesterBased, terms, selectedTerm]);

  // Fetch classes when context changes
  useEffect(() => {
    if (selectedAcademicYear && selectedProgramId) {
      fetchClasses();
    }
  }, [selectedAcademicYear, selectedProgramId, isSemesterBased, selectedSemester, selectedTerm, fetchClasses]);

  // Fetch mapping when context changes
  useEffect(() => {
    fetchProgramMapping();
  }, [
    tenantId,
    selectedProgramId,
    selectedProgramTypeId,
    selectedAcademicYear,
    selectedSemester,
    selectedTerm,
    isSemesterBased,
    fetchProgramMapping
  ]);

  // Notify parent of context changes
  useEffect(() => {
    const context = {
      programTypeId: selectedProgramTypeId,
      programId: selectedProgramId,
      academicYearId: selectedAcademicYear,
      semesterId: isSemesterBased ? selectedSemester : null,
      termId: !isSemesterBased ? selectedTerm : null,
      isSemesterBased,
      hasExistingMapping: !!programMapping,
      programMapping
    };
    
    // This will be used by the parent component
    if (onContextChange) {
      onContextChange(context);
    }
  }, [selectedProgramTypeId, selectedProgramId, selectedAcademicYear, 
      selectedSemester, onContextChange, selectedTerm, isSemesterBased, programMapping]);

  return {
    academicYears: activeTab === 'active' ? academicYears : completedAcademicYears,
    completedAcademicYears,
    selectedAcademicYear,
    setSelectedAcademicYear,
    isSemesterBased,
    semesters: activeTab === 'active' ? semesters : completedSemesters,
    completedSemesters,
    selectedSemester,
    setSelectedSemester,
    terms: activeTab === 'active' ? terms : completedTerms,
    completedTerms,
    selectedTerm,
    setSelectedTerm,
    classes,
    selectedClassId,
    setSelectedClassId,
    activeTab,
    setActiveTab: switchTab,
    loading,
    fetchClasses,
    isViewOnly: isViewOnly(),
    selectedAcademicYearData: getSelectedAcademicYearData(),
    selectedSemesterData: getSelectedSemesterData(),
    selectedTermData: getSelectedTermData(),
    programTypes,
    setProgramTypes,
    selectedProgramTypeId,
    setSelectedProgramTypeId,
    programs,
    setPrograms,
    selectedProgramId,
    setSelectedProgramId,
    programMapping,
    setProgramMapping
  };
};